import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Sparkles, Loader2, MapPin, BookOpen, Heart } from 'lucide-react';
import { DISCOVER } from '@/constants/testIds';
import { toast } from 'sonner';
import axios from 'axios';

const Discover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiMatches, setAiMatches] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dobRequired, setDobRequired] = useState(false);

  // Build a compact "You & them" highlight based on the caller's user record.
  const buildHighlight = (match) => {
    const bits = [];
    // Shared genres — use common_genres from backend when present, else derive
    const sharedGenres =
      Array.isArray(match.common_genres)
        ? match.common_genres.length
        : (user?.favorite_genres || []).filter(g => (match.favorite_genres || []).includes(g)).length;
    if (sharedGenres > 0) bits.push(`${sharedGenres} shared ${sharedGenres === 1 ? 'genre' : 'genres'}`);

    // Books in common — only exposed as a total count on the browse endpoint (match.books_read)
    if (typeof match.books_read === 'number' && match.books_read > 0) {
      bits.push(`${match.books_read} ${match.books_read === 1 ? 'book' : 'books'} read`);
    }

    // Age gap
    if (user?.age != null && match.age != null) {
      const diff = Math.abs(user.age - match.age);
      bits.push(diff === 0 ? 'same age' : `${diff} yr${diff === 1 ? '' : 's'} apart`);
    }

    // Same city
    const myCity = (user?.location?.city || '').trim().toLowerCase();
    const theirCity = (match.location?.city || '').trim().toLowerCase();
    if (myCity && theirCity && myCity === theirCity) bits.push('same city');

    return bits;
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/matches`,
        { withCredentials: true }
      );
      setMatches(data);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.toLowerCase().includes('date of birth')) {
        setDobRequired(true);
      } else {
        toast.error('Failed to load matches');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAIMatches = async () => {
    setAiLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/matches/ai-suggest`,
        { withCredentials: true }
      );
      setAiMatches(data.matches || []);
      toast.success('AI matches generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI matches');
    } finally {
      setAiLoading(false);
    }
  };

  const sendConnection = async (userId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/connections/request`,
        { target_user_id: userId, message: 'Hi! Would love to connect and share book recommendations!' },
        { withCredentials: true }
      );
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const filteredMatches = matches.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.favorite_genres.some(g => g.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-6 py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-[#5C5955] hover:text-[#6B7A4F]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div data-testid={DISCOVER.container} className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-4">
              Discover Your <span className="font-bold">Bookish Bestie</span>
            </h1>
            {dobRequired && (
              <div
                data-testid="discover-dob-required-banner"
                className="mb-4 bg-[#E2E6D5]/60 border-2 border-[#6B7A4F]/40 rounded-2xl p-5"
              >
                <h3 className="font-semibold text-[#2D2A26] mb-1">Add your date of birth to see matches</h3>
                <p className="text-sm text-[#5C5955] mb-3">
                  Bookish Bestie is 18+ only and matches you with readers within an 8-year age range. Please add your date of birth to unlock matching.
                </p>
                <Button
                  onClick={() => navigate('/profile')}
                  className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full px-5"
                  data-testid="discover-add-dob-btn"
                >
                  Add Date of Birth
                </Button>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                placeholder="Search by name or genre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] rounded-xl"
              />
              <Button
                data-testid={DISCOVER.aiMatchBtn}
                onClick={fetchAIMatches}
                disabled={aiLoading}
                className="bg-gradient-to-r from-[#6B7A4F] to-[#8E9E82] hover:opacity-90 text-white rounded-full px-6"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    AI Suggest
                  </>
                )}
              </Button>
            </div>
          </div>

          {aiMatches.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#2D2A26] mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#8E9E82]" />
                AI Recommendations
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiMatches.map((match) => (
                  <div key={match._id} className="match-card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-xl text-[#2D2A26]">
                          {match.name}
                          {match.age != null && (
                            <span className="text-sm font-normal text-[#5C5955] ml-2">· {match.age}</span>
                          )}
                        </h3>
                        {match.location?.city && (
                          <p className="text-sm text-[#5C5955] flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {match.location.city}
                          </p>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const bits = buildHighlight(match);
                      return bits.length > 0 && (
                        <div
                          data-testid="you-and-them-highlight"
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7A4F] font-semibold uppercase tracking-wider mb-3"
                        >
                          <Heart className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {bits.map((b, i) => (
                            <React.Fragment key={i}>
                              <span>{b}</span>
                              {i < bits.length - 1 && <span className="text-[#6B7A4F]/40">·</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    })()}
                    {match.bio && (
                      <p className="text-sm text-[#5C5955] mb-3">{match.bio}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {match.favorite_genres.slice(0, 3).map((genre) => (
                        <span key={genre} className="genre-badge">{genre}</span>
                      ))}
                    </div>
                    <div className="bg-[#E2E6D5]/50 rounded-lg p-3 mb-3">
                      <p className="text-xs text-[#2D2A26] italic">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        {match.ai_reason}
                      </p>
                    </div>
                    <Button
                      onClick={() => sendConnection(match._id)}
                      className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Connect
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-semibold text-[#2D2A26] mb-4">
              All Matches ({filteredMatches.length})
            </h2>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" />
              </div>
            ) : filteredMatches.length === 0 ? (
              <div className="text-center py-12 glass-card rounded-2xl">
                <BookOpen className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">No matches found</h3>
                <p className="text-[#5C5955]">Complete your profile and add books to find matches</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMatches.map((match) => (
                  <div key={match._id} data-testid={DISCOVER.matchCard} className="match-card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-xl text-[#2D2A26]">
                          {match.name}
                          {match.age != null && (
                            <span className="text-sm font-normal text-[#5C5955] ml-2">· {match.age}</span>
                          )}
                        </h3>
                        {match.location?.city && (
                          <p className="text-sm text-[#5C5955] flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {match.location.city}
                          </p>
                        )}
                      </div>
                      <div data-testid={DISCOVER.compatibilityScore} className="compatibility-badge">
                        {match.compatibility_score}%
                      </div>
                    </div>
                    {(() => {
                      const bits = buildHighlight(match);
                      return bits.length > 0 && (
                        <div
                          data-testid="you-and-them-highlight"
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7A4F] font-semibold uppercase tracking-wider mb-3"
                        >
                          <Heart className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {bits.map((b, i) => (
                            <React.Fragment key={i}>
                              <span>{b}</span>
                              {i < bits.length - 1 && <span className="text-[#6B7A4F]/40">·</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    })()}
                    {match.bio && (
                      <p className="text-sm text-[#5C5955] mb-3 line-clamp-2">{match.bio}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {match.favorite_genres.slice(0, 3).map((genre) => (
                        <span key={genre} className="genre-badge">{genre}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm text-[#5C5955] mb-3">
                      <span>{match.books_read} books</span>
                      {match.common_genres.length > 0 && (
                        <span className="text-[#6B7A4F] font-semibold">
                          {match.common_genres.length} common genres
                        </span>
                      )}
                    </div>
                    <Button
                      data-testid={DISCOVER.connectBtn}
                      onClick={() => sendConnection(match._id)}
                      className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Send Request
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Discover;
